import {Lang} from 'yougile-lang'
import {logger, Util} from 'yougile-util'
import {KeyResp} from 'yougile-model'
import {Refresh} from '@client/refresh'
import {Backend} from '@common/backend'
import {TrackingEvent} from '@common/tracking-events'
import {globals} from '@extensions/globals-base'
import {Google} from './google-api'
import {Tracking} from './tracking'

type ShowAlertFunction = (message: string) => Promise<void>

const state = {
  isInitialized: false,
  inProgress: false
}

const getLocation = () => {
  if (location.pathname === '/team/') {
    return 'login'
  }
  if (document.querySelector('.s-reg-show')) {
    return 'landingPopup'
  } else {
    return 'landing'
  }
}

const handleCredentialResponse = async (
  response: any,
  showAlert: ShowAlertFunction | undefined
): Promise<void> => {
  void Tracking.send(TrackingEvent.regFormGoogleClick, {
    location: getLocation()
  })
  if (state.inProgress) {
    logger.log('Already running google callback, skipping...')
    Backend.report('Running google callback while previous was in progress')
    return
  }
  state.inProgress = true

  const res = await Backend.postJSON<KeyResp>(
    '/data/google-signin/login',
    {
      token: response.credential,
      lang: Lang.getLang()
    },
    {useCurrentHost: true}
  ).catch(e => ({error: e}) as KeyResp)

  state.inProgress = false

  if (res.error || !res.key || !res.userId) {
    void Tracking.send(TrackingEvent.regFormGoogleError)
    let errorMsg = Lang.msg.reg.failSignInWithGoogle
    if (res.code === 601) {
      errorMsg = Lang.msg.reg.accountDisabledGoogle
    }

    void showAlert?.(errorMsg)
    logger.error(res.error, res.code)
  } else {
    Backend.saveCredentials(res.userId, res.key)
    location.href = '/team/'
  }
}

export const getGoogleClientId = (hostname: string): string | undefined => {
  const devId = globals.googleOAuthClientIdDev
  const testId = globals.googleOAuthClientIdTest
  const prodId = globals.googleOAuthClientIdProd
  return hostname.startsWith('test') && hostname.endsWith('.yougile.com')
    ? testId
    : hostname === 'yougile.com' || hostname.endsWith('.yougile.com')
      ? prodId
      : hostname === 'localhost'
        ? devId
        : undefined
}

export const addGoogleSignIn = (
  showAlert: ShowAlertFunction | undefined,
  retryCount = 0
): boolean => {
  try {
    if (state.isInitialized) {
      return true
    }
    const google = (window as any).google as Google
    if (!google) {
      if (retryCount < 5) {
        setTimeout(() => addGoogleSignIn(showAlert, retryCount + 1), 500)
      } else {
        logger.error('google is not set after 5 retries')
      }
      return false
    }
    const clientId = getGoogleClientId(location.hostname)
    if (!clientId) {
      return false
    }
    const buttons = document.querySelectorAll<HTMLElement>('.s-reg-google')

    if (buttons.length) {
      google.accounts.id.initialize({
        client_id: clientId,
        callback: (response: any) =>
          handleCredentialResponse(response, showAlert)
      })
      state.isInitialized = true
    }

    for (const btn of Util.iter(buttons)) {
      google.accounts.id.renderButton(btn, {
        type: 'standard',
        shape: 'pill',
        theme: 'filled_black',
        text: 'signin_with',
        size: 'large',
        locale: 'ru',
        logo_alignment: 'left',
        width: 250
      })
    }

    return !!buttons.length
  } catch (e) {
    logger.log('Error in google sign in', e)
    return false
  }
}

export const resetGoogleSignInState = (): void => {
  state.isInitialized = false
  state.inProgress = false
}

export const approveSignInCode = async (
  code: string,
  showAlert: ShowAlertFunction
): Promise<void> => {
  const res = await Backend.postJSON<KeyResp>(
    '/data/google-signin/approve-code',
    {code},
    {useCurrentHost: true}
  ).catch(e => ({error: e}) as KeyResp)
  if (res.error || !res.key || !res.userId) {
    void showAlert?.(Lang.msg.reg.failSignInWithGoogle)
    logger.error(res.error)
  } else {
    Backend.saveCredentials(res.userId, res.key)
    Refresh.refresh()
  }
}
